import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  Zap,
  MessageSquare,
  Pin,
  PinOff,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  icon: LucideIcon;
  path: string;
  label: string;
}

const navItems: NavItem[] = [
  { icon: LayoutDashboard, path: '/billing', label: 'Dashboard' },
  { icon: Zap, path: '/billing/ai-usage', label: 'AI Abrechnung' },
  { icon: MessageSquare, path: '/billing/messaging', label: 'WhatsApp Abrechnung' },
  { icon: Receipt, path: '/billing/invoices', label: 'Rechnungen' },
];

export const BILLING_SIDEBAR_STORAGE_KEY = 'vitbikes-billing-sidebar-expanded';

export interface BillingSidebarProps {
  isExpanded: boolean;
  isPinned?: boolean;
  onToggle: () => void;
}

export function BillingSidebar({ isExpanded, isPinned = false, onToggle }: BillingSidebarProps) {
  const location = useLocation();

  return (
    <aside className="h-full bg-slate-800 flex flex-col overflow-hidden">
      {/* Logo & Brand */}
      <div className="h-16 flex items-center border-b border-slate-700 px-3">
        <div className={cn(
          "flex items-center gap-3 transition-all duration-300",
          isExpanded ? "justify-start" : "justify-center w-full"
        )}>
          <img
            src="/fav.png"
            alt="vit:bikes"
            className="w-10 h-10 object-contain shrink-0"
          />
          {isExpanded && (
            <span className="text-lg font-bold text-white whitespace-nowrap overflow-hidden animate-fade-in">
              Billing
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => {
            const isActive = item.path === '/billing'
              ? location.pathname === '/billing' || location.pathname === '/billing/'
              : location.pathname.startsWith(item.path);

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={cn(
                    'flex items-center h-12 rounded-lg transition-colors relative group',
                    isExpanded ? 'px-3 gap-3' : 'justify-center',
                    isActive
                      ? 'bg-slate-700 text-orange-500'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                  )}
                  title={!isExpanded ? item.label : undefined}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-orange-500 rounded-r-full" />
                  )}
                  <item.icon className="w-5 h-5 shrink-0" />

                  {isExpanded && (
                    <span className="text-sm font-medium whitespace-nowrap overflow-hidden">
                      {item.label}
                    </span>
                  )}

                  {!isExpanded && (
                    <span className="absolute left-full ml-2 px-2 py-1 bg-slate-700 text-slate-200 text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Pin/Unpin Button */}
      <div className="px-2 py-2 border-t border-slate-700">
        <button
          onClick={onToggle}
          className={cn(
            "flex items-center h-10 rounded-lg transition-colors w-full",
            isPinned
              ? "text-orange-400 hover:bg-slate-700/50 hover:text-orange-300"
              : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200",
            isExpanded ? "px-3 gap-3 justify-start" : "justify-center"
          )}
          title={isPinned ? "Sidebar lösen" : "Sidebar fixieren"}
        >
          {isPinned ? (
            <>
              <PinOff className="w-5 h-5 shrink-0" />
              {isExpanded && <span className="text-sm font-medium">Lösen</span>}
            </>
          ) : (
            <>
              <Pin className="w-5 h-5 shrink-0" />
              {isExpanded && <span className="text-sm font-medium">Fixieren</span>}
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
